// Page Builder Vuex Module
// This module manages the page structure data including nested columns

const state = {
	pageStructure: [],
	unsavedChanges: false
}

const mutations = {
	SET_PAGE_STRUCTURE(state, structure) {
		state.pageStructure = structure
	},
	UPDATE_NESTED_COMPONENT(state, { path, components }) {
		// Handle updating nested components by path
		// path is an array like [0, 'properties', 'columns', 1, 'components']
		let target = state.pageStructure

		// Navigate to the target array using the path
		for (let i = 0; i < path.length - 1; i++) {
			target = target[path[i]]
		}

		// Update the target array
		target[path[path.length - 1]] = components

		// Mark as having unsaved changes
		state.unsavedChanges = true
	},
	UPDATE_NESTED_COMPONENTS_AT_PATH(state, { path, components }) {
		// Similar to UPDATE_NESTED_COMPONENT but more explicit for handling column components
		try {
			let target = state.pageStructure

			// Navigate to the target using the provided path
			for (let i = 0; i < path.length - 1; i++) {
				if (target[path[i]] === undefined) {
					console.error(`Path segment ${path[i]} not found at depth ${i}`, target)
					return
				}
				target = target[path[i]]
			}

			// Update the components at the specified path
			target[path[path.length - 1]] = components

			// Mark as having unsaved changes
			state.unsavedChanges = true
		} catch (error) {
			console.error('Error updating nested components:', error)
		}
	},
	UPDATE_ELEMENT_AT_INDEX(state, { index, properties }) {
		// Update an element at a specific index in the page structure
		if (state.pageStructure[index]) {
			// Merge new properties with existing properties
			state.pageStructure[index].properties = {
				...state.pageStructure[index].properties,
				...properties
			}

			// Mark as having unsaved changes
			state.unsavedChanges = true
		}
	},
	SET_UNSAVED_CHANGES(state, value) {
		state.unsavedChanges = value
	}
}

const actions = {
	setPageStructure({ commit }, structure) {
		commit('SET_PAGE_STRUCTURE', structure)
		commit('SET_UNSAVED_CHANGES', false)
	},
	updateNestedComponent({ commit }, { path, components }) {
		commit('UPDATE_NESTED_COMPONENT', { path, components })
	},
	updateNestedComponentsAtPath({ commit }, { path, components }) {
		commit('UPDATE_NESTED_COMPONENTS_AT_PATH', { path, components })
	},
	updateElementAtIndex({ commit }, { index, properties }) {
		commit('UPDATE_ELEMENT_AT_INDEX', { index, properties })
	},
	setUnsavedChanges({ commit }, value) {
		commit('SET_UNSAVED_CHANGES', value)
	}
}

const getters = {
	getPageStructure: (state) => state.pageStructure,
	hasUnsavedChanges: (state) => state.unsavedChanges,

	// Helper getter to find a component by euid anywhere in the structure
	findComponentByEuid: (state) => (euid) => {
		const findInStructure = (structure, basePath = []) => {
			for (let i = 0; i < structure.length; i++) {
				const item = structure[i]
				const currentPath = [...basePath, i]

				if (item.euid === euid) {
					return { item, path: currentPath }
				}

				// Check for BasicLayout that has columns
				if (item.type === 'BasicLayout' && item.properties && item.properties.columns) {
					for (let j = 0; j < item.properties.columns.length; j++) {
						const column = item.properties.columns[j]

						// Skip if no components array
						if (!column.components || !Array.isArray(column.components)) {
							continue
						}

						// Check components in this column
						for (let k = 0; k < column.components.length; k++) {
							const component = column.components[k]

							if (component.euid === euid) {
								return {
									item: component,
									path: [...currentPath, 'properties', 'columns', j, 'components', k]
								}
							}

							// Recursive check for nested BasicLayouts
							if (component.type === 'BasicLayout' && component.properties?.columns) {
								const nestedResult = findInNestedLayout(component, [
									...currentPath,
									'properties',
									'columns',
									j,
									'components',
									k
								])
								if (nestedResult) return nestedResult
							}
						}
					}
				}
			}
			return null
		}

		// Helper function to search within nested BasicLayouts
		const findInNestedLayout = (layout, basePath) => {
			if (!layout.properties?.columns) return null

			for (let j = 0; j < layout.properties.columns.length; j++) {
				const column = layout.properties.columns[j]

				// Skip if no components array
				if (!column.components || !Array.isArray(column.components)) {
					continue
				}

				// Check components in this column
				for (let k = 0; k < column.components.length; k++) {
					const component = column.components[k]

					if (component.euid === euid) {
						return {
							item: component,
							path: [...basePath, 'properties', 'columns', j, 'components', k]
						}
					}

					// Another level of nesting - recursive call
					if (component.type === 'BasicLayout' && component.properties?.columns) {
						const nestedResult = findInNestedLayout(component, [
							...basePath,
							'properties',
							'columns',
							j,
							'components',
							k
						])
						if (nestedResult) return nestedResult
					}
				}
			}
			return null
		}

		return findInStructure(state.pageStructure)
	}
}

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}
